import { SwitchOptions } from "@/components/SwitchOptions";
import { formatLocaleCurrency, formatLocaleNumber } from "@/utils/formatUtils";
import { getDatePickerDefaultRanges, getDatePickerFormat } from "@/utils/formUtils";
import { cssVariables, dateToIsoDateString } from "@/utils/utils";
import { Card, Col, DatePicker, Empty, Flex, Row } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useMemo, useState } from "react";
import CountUp from "react-countup";
import { useTranslation } from "react-i18next";
import { Pie, PieChart, ResponsiveContainer } from "recharts";
import { useGetDashboardContractStatistics } from "../../../../../common/api/queries";
import { ChartItem } from "../../../../../common/types";
import { ProductFinancialSector } from "../../../../product/enums";
import { DATA_UNIT } from "../../../enums";
import { useChartActiveShapeRender } from "./utils";

const chartColors = [
  cssVariables.colorBlue,
  cssVariables.colorOrange,
  cssVariables.colorGreen,
  cssVariables.colorYellow,
  "darkcyan",
  "hotpink",
  "brown",
  cssVariables.colorRed,
];

const sectorColors: Record<ProductFinancialSector, string | undefined> = {
  [ProductFinancialSector.NON_LIFE_INSURANCES]: chartColors[0],
  [ProductFinancialSector.LIFE_INSURANCES]: chartColors[1],
  [ProductFinancialSector.LOANS_AND_MORTGAGES]: chartColors[2],
  [ProductFinancialSector.CAPITAL_MARKET]: chartColors[3],
  [ProductFinancialSector.DEPOSITS]: chartColors[4],
  [ProductFinancialSector.SENIOR_PENSION_SAVINGS]: chartColors[5],
  [ProductFinancialSector.SUPPLEMENTARY_PENSION_SAVINGS]: chartColors[6],
  [ProductFinancialSector.OTHERS]: chartColors[7],
  [ProductFinancialSector.ALL]: "black",
};

enum CHART_TYPE {
  OWN = "OWN",
  ALL = "ALL",
}

export const DashboardContractStatisticsWidget = () => {
  const { t } = useTranslation();

  const { onMouseEnter, renderShape } = useChartActiveShapeRender();

  const [chartDates, setChartDates] = useState<[Dayjs, Dayjs]>(() => [dayjs().startOf("month"), dayjs()]);
  const [chartType, setChartType] = useState<CHART_TYPE>(CHART_TYPE.ALL);
  const [chartUnit, setChartUnit] = useState<DATA_UNIT>(DATA_UNIT.AMOUNT_SUM);

  const { data: contractStatistics } = useGetDashboardContractStatistics({
    minDate: dateToIsoDateString(chartDates[0]),
    maxDate: dateToIsoDateString(chartDates[1]),
  });

  const chartData = useMemo((): ChartItem[] => {
    if (!contractStatistics) {
      return [];
    }

    let items: Record<ProductFinancialSector, number>;

    if (chartType === CHART_TYPE.OWN) {
      items =
        chartUnit === DATA_UNIT.CONTRACT_COUNT
          ? contractStatistics.ownContractsCounts
          : contractStatistics.ownContractsAmountsSum;
    } else {
      items =
        chartUnit === DATA_UNIT.CONTRACT_COUNT
          ? contractStatistics.allContractsCounts
          : contractStatistics.allContractsAmountsSum;
    }

    return Object.keys(ProductFinancialSector)
      .filter((sector) => sector in items)
      .map<ChartItem>((sector) => {
        const value = items[sector as keyof typeof ProductFinancialSector];
        const formattedValue =
          chartUnit === DATA_UNIT.CONTRACT_COUNT ? formatLocaleNumber(value) : formatLocaleCurrency(value, 0);

        return {
          key: sector,
          label: t(`dashboard.contractStatistics.enums.financialSector.${sector}`),
          value,
          formattedValue,
        };
      });
  }, [chartType, chartUnit, contractStatistics]);

  return (
    <Flex vertical style={{ height: "100%" }}>
      <Flex justify="space-between">
        <h2 style={{ fontWeight: 600, fontSize: 28, lineHeight: "36px" }}>{t("dashboard.contractStatistics.title")}</h2>

        <DatePicker.RangePicker
          style={{ borderRadius: 18, marginBottom: 14 }}
          format={getDatePickerFormat()}
          allowClear={false}
          presets={getDatePickerDefaultRanges({ maxDate: dayjs(), hasToday: true })}
          maxDate={dayjs()}
          placeholder={[t("common.from"), t("common.to")]}
          defaultPickerValue={[dayjs().startOf("month"), dayjs()]}
          value={chartDates}
          onChange={(dates) => (dates ? setChartDates(dates as [Dayjs, Dayjs]) : undefined)}
        />
      </Flex>

      <Row style={{ height: "100%", overflow: "hidden" }}>
        <Col span={6} style={{ maxHeight: "100%" }}>
          <Flex vertical justify="space-between" style={{ height: "100%" }}>
            <div style={{ padding: "24px 12px 24px 0", position: "relative" }}>
              <SwitchOptions
                vertical
                options={[
                  { value: CHART_TYPE.ALL, label: t("dashboard.contractStatistics.enums.chartType.ALL") },
                  { value: CHART_TYPE.OWN, label: t("dashboard.contractStatistics.enums.chartType.OWN") },
                ]}
                onChange={(value) => setChartType(value)}
                value={chartType}
              />

              <div style={{ position: "absolute", width: 20, height: 20, right: 0, bottom: 0 }}>
                <svg width="20" height="20" viewBox="0 0 100 100">
                  <path d="M0 100 Q100 100 100 0 L100 100 Z" fill="white" />
                </svg>
              </div>
            </div>

            <Flex
              vertical
              flex={1}
              style={{
                backgroundColor: "white",
                borderTopLeftRadius: 18,
                borderBottomLeftRadius: 18,
                padding: "24px 0 24px 24px",
                overflow: "hidden",
              }}
            >
              <div style={{ overflowY: "auto", flex: 1 }}>
                {chartData.map((item) => (
                  <div key={item.key} style={{ marginBottom: 16 }}>
                    <div>{item.label}</div>
                    <div
                      style={{ color: sectorColors[item.key as ProductFinancialSector], fontWeight: 600, fontSize: 21 }}
                    >
                      <CountUp
                        end={item.value}
                        separator={" "}
                        duration={4}
                        suffix={chartUnit === DATA_UNIT.AMOUNT_SUM ? " €" : ""}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </Flex>
          </Flex>
        </Col>

        <Col span={18}>
          <Card
            style={{ borderRadius: "18px 18px 18px 0", height: "100%", border: "none" }}
            styles={{ body: { height: "100%" } }}
          >
            <Flex vertical justify="space-between" style={{ height: "100%" }}>
              <Flex flex={1} justify="center" align="center">
                {chartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 200, height: 220 }}>
                    <PieChart>
                      <Pie
                        innerRadius={70}
                        paddingAngle={4}
                        cx="50%"
                        cy="50%"
                        dataKey="value"
                        nameKey="label"
                        data={chartData.map((item) => ({
                          ...item,
                          fill: sectorColors[item.key as keyof typeof sectorColors],
                        }))}
                        shape={renderShape}
                        onMouseEnter={onMouseEnter}
                        legendType="none"
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )}
              </Flex>
              <div style={{ width: 200, alignSelf: "end" }}>
                <SwitchOptions
                  size="small"
                  options={[
                    {
                      value: DATA_UNIT.AMOUNT_SUM,
                      label: t("dashboard.enums.dataUnit.AMOUNT_SUM"),
                    },
                    { value: DATA_UNIT.CONTRACT_COUNT, label: t("dashboard.enums.dataUnit.CONTRACT_COUNT") },
                  ]}
                  onChange={(value) => setChartUnit(value)}
                  value={chartUnit}
                />
              </div>
            </Flex>
          </Card>
        </Col>
      </Row>
    </Flex>
  );
};
